/** @jsxImportSource @emotion/react */
import css from '@emotion/css/macro'
import scheduleActions from '@sportninja/common/actions/schedule'
import { bindActionToPromise } from '@sportninja/common/actions/utils'
import req from '@sportninja/common/api/request'
import { ENABLE_REGISTRATION, ROUTES } from '@sportninja/common/constants/app'
import { t } from '@sportninja/common/i18n'
import { getImageThumbnailId } from '@sportninja/common/reducers/helpers'
import parsers from '@sportninja/common/reducers/parsers'
import { convertTimesToISOStrings } from '@sportninja/common/sagas/game'
import { ENTITY_TYPES } from '@sportninja/common/sagas/utils'
import {
  getHockeySport,
  getSoccerSport,
} from '@sportninja/common/selectors/types'
import React, { useCallback, useContext, useEffect, useRef, useState } from 'react'
import { connect } from 'react-redux'
import { useHistory } from 'react-router-dom'

import FileDownload from '../../components/FileDownload'
import EntityFormDelete from '../../components/Form/EntityFormDelete'
import FormSheet from '../../components/Form/FormSheet'
import {
  feedItemForm,
  gameForm,
  scheduleForm,
  venueForm,
} from '../../components/Form/form-configs'
import ImportCSV from '../../components/ImportCSV'
import GameList from '../../components/List/GameList'
import OfficialList, { AddOfficial } from '../../components/List/OfficialList'
import UserList, { AddUser } from '../../components/List/UserList'
import Page from '../../components/Page'
import BaseActionButton from '../../components/Page/BaseActionButton'
import EditButton from '../../components/Page/EditButton'
import FavouriteButton from '../../components/Page/FavouriteButton'
import NewButton from '../../components/Page/NewButton'
import { Mobile } from '../../components/Responsive'
import { ScheduleReprocessingContext } from '../../components/ScheduleReprocessingContext'
import Standings from '../../components/Standings'
import Statistics from '../../components/Statistics'
import Suspensions from '../../components/Suspensions'
import AddSuspensionSheet from '../../components/Suspensions/AddSuspensionSheet'
import useGetSuspensions from '../../components/Suspensions/useGetSuspensions'
import Venues from '../../components/Venues'
import { FrameContext } from '../App'
import EditEntitySubTitle from '../EditEntitySubTitle'
import Home from '../Home'
import { getBreadcrumbs, useReadItem, useStoreRecent } from '../utils'
import GameSlots from './GameSlots'
import ScheduleBracket from './ScheduleBracket'
import ScheduleTree from './ScheduleTree'
// import CreateBrackets from './CreateBrackets'
import { isCanlan } from '@sportninja/common/utils/customer-name'
import CloneSchedule from './CloneSchedule'
import ReprocessStats from './ReprocessStats'
import Registration from './Registration'
import EditRegistration from './EditRegistration'
import NewReports from '../../components/NewReports'
import LoadingSpinner from 'src/components/LoadingSpinner'
import { NewLocationAddressSelector } from 'src/components/NewLocationAddressSelector'
import Icon from 'src/components/Icon'
import { NewModal } from 'src/components/NewModal'
import colors from '@sportninja/common/constants/appColors'

const generateHrefAndDownloadCsv = (string, reportName) => {
  var blob = new Blob([string], { type: 'text/csv;charset=utf-8;' })

  if (navigator.msSaveBlob) {
    // IE 10+
    navigator.msSaveBlob(blob, reportName)
  } else {
    var a = document.createElement('a')
    document.body.appendChild(a)
    a.style = 'display: none'
    var csvUrl = URL.createObjectURL(blob)
    a.href = csvUrl
    a.download = reportName
    a.click()
    URL.revokeObjectURL(a.href)
    a.remove()
  }
}

const Schedule = ({
  match,
  createVenue,
  hockeySportId,
  isInFrame,
  soccerSportId,
}) => {
  const frameConfig = useContext(FrameContext)
  const history = useHistory()

  const {
    loading,
    error,
    item,
    hierarchy,
    permission,
    readItem,
    refreshKey,
    triggerRefresh,
    features,
  } = useReadItem()

  const isSoccer = item?.sport_id ? item?.sport_id === soccerSportId : false
  const SCHEDULE_ID = match.params.id
  const BASE_ROUTE = `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}`
  const IS_PUBLIC = item.is_public
  const TOURNAMENT_READY = item.is_tournament
  const syncSalesforce = item?.organization?.sync_salesforce || false
  const canSyncGameSlots = item?.tournament_can_sync_regular_season

  const [visibleGameIds, setVisibleGameIds] = useState([])
  const [areFiltersHidden, setAreFiltersHidden] = useState(false)
  const [isFilterToggleDisabled, setIsFilterToggleDisabled] = useState(false)
  const [filterForExporting, setFilterForExporting] = useState(null)
  const [ifNecessaryStatus, setIfNecessaryStatus] = useState(false)
  const [selectedGameSlotDivision, setSelectedGameSlotDivision] = useState(null)
  const [selectedGameSlotDivisionName, setSelectedGameSlotDivisionName] =
    useState(null)
  const [gamesCreated, setGamesCreated] = useState(false)
  const [hasTournamentStarted, setHasTournamentStarted] = useState(false)
  const [onFinishUploadGameslots, setOnFinishUploadGameslots] = useState(false)
  const [isLoadingVenue, setIsLoadingVenue] = useState(false)

  useStoreRecent(ENTITY_TYPES.schedule, item, error, SCHEDULE_ID, isInFrame)

  useEffect(() => {
    readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
  }, [SCHEDULE_ID])

  // const locationModalRef = useRef(null)

  const { listeners = {} } = useContext(ScheduleReprocessingContext)
  const scheduleReprocessingListener = listeners[SCHEDULE_ID]

  const {
    loading: isSuspensionsLoading,
    suspensions,
    refresh,
  } = useGetSuspensions(SCHEDULE_ID)

  const onEditSchedule = async (...args) => {
    const [values] = args
    const { imageData, ...form } = values

    const shouldDeleteImage = values?.delete_image ?? false

    if (form?.name_full) form.name = form.name_full

    const body = JSON.stringify(form)
    const response = await req(`/schedules/${item.id}`, {
      method: 'PUT',
      body,
    })

    if (shouldDeleteImage) {
      await req(`/schedules/${item.id}/image`, {
        method: 'DELETE',
      })
    }

    if (imageData) {
      await req(`/schedules/${item.id}/image`, {
        method: 'POST',
        body: imageData,
      })
    }
    await readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
    return response
  }

  const onAddVenue = async (...args) => {
    const response = await createVenue(SCHEDULE_ID, ...args)
    triggerRefresh()
    return response
  }

  // const onAddVenue = useCallback(async (data) => {
  //   setIsLoadingVenue(true)

  //   const body = {
  //     address_line1: data?.args?.address_line1,
  //     address_line2: data?.args?.address_line2,
  //     city: data?.args?.city,
  //     postcode: data?.args?.postcode,
  //     country: data?.args?.country,
  //     state: data?.args?.state,
  //     name: data?.args?.name,
  //     timezone: data?.args?.timezone,
  //     latitude: data?.args?.latitude,
  //     longitude: data?.args?.longitude,
  //     state_code: data?.args?.state_code,
  //   }

  //   try {
  //     const response = await req(`/schedules/${SCHEDULE_ID}/venues`, {
  //       method: 'POST',
  //       body: JSON.stringify(body),
  //     })

  //     if (response?.data?.id) {
  //       for (let index = 0; index < args?.facilities?.length; index++) {
  //         const facility = args?.facilities[index]

  //         const facilityBody = {
  //           name: facility?.name,
  //         }

  //         await req(
  //           `/schedules/${SCHEDULE_ID}/venues/${response?.data?.id}/facilities`,
  //           {
  //             method: 'POST',
  //             body: JSON.stringify(facilityBody),
  //           }
  //         )
  //       }
  //     }
  //   } catch (error) {
  //     console.log('ERROR', error)
  //   } finally {
  //     locationModalRef.current.closeModal()
  //     setIsLoadingVenue(false)
  //     triggerRefresh()
  //   }
  // }, [])

  const onAddGame = async ({ ...form }) => {
    const body = convertTimesToISOStrings(form, [
      'starts_at',
      'started_at',
      'ends_at',
      'ended_at',
    ])

    // For web: if no game_type_id was selected, set the default value as 2 (i.e. regular season)
    if (!body.game_type_id) body.game_type_id = 2
    else if (Object.prototype.hasOwnProperty.call(body, 'game_type_id')) {
      body.game_type_id = parseInt(body.game_type_id, 10)
    }

    const response = await req(`/schedules/${SCHEDULE_ID}/games`, {
      method: 'POST',
      body: JSON.stringify({
        ...body,
        if_necessary_status: ifNecessaryStatus,
      }),
    })

    triggerRefresh()
    return response
  }

  const titleActions = item?.id
    ? [
        <FavouriteButton
          key='favourite'
          initialState={item.is_favorite}
          entityType={ENTITY_TYPES.schedule}
          entityId={SCHEDULE_ID}
        />,
        permission.update && (
          <FormSheet
            customDeleteModal={
              permission.delete && (
                <EntityFormDelete
                  entityType={ENTITY_TYPES.schedule}
                  itemId={SCHEDULE_ID}
                  title={`Delete ${item.name_full}`}
                  type={t('common:competition')}
                  onComplete={() => {
                    history.push(`${ROUTES.SCHEDULES.ROOT}`)
                  }}
                />
              )
            }
            key='edit'
            Button={({ onClick }) => (
              <EditButton onClick={onClick} title={item?.name_full} />
            )}
            form={scheduleForm(
              parsers[ENTITY_TYPES.schedule](item),
              false,
              syncSalesforce,
              hockeySportId
            )}
            label={t('Web:editCompetition')}
            onSubmit={onEditSchedule}
            title={t('Web:editCompetition')}
            subTitle={
              <div>
                <EditEntitySubTitle
                  id={SCHEDULE_ID}
                  entityType={ENTITY_TYPES.schedule}
                />
                {isCanlan && syncSalesforce && item?.salesforce_id ? (
                  <EditEntitySubTitle
                    id={item?.salesforce_id}
                    entityType={ENTITY_TYPES.salesforce}
                  />
                ) : null}
              </div>
            }
          />
        ),
      ]
    : []

  const baseActions = []

  const filterToggleButton = !isFilterToggleDisabled && (
    <BaseActionButton
      faType={areFiltersHidden ? 'far' : 'fas'}
      key='toggle-filter'
      name='filter'
      title={`Toggle Filters Visibility ${areFiltersHidden ? 'on' : 'off'}`}
      onClick={() => {
        setAreFiltersHidden((v) => !v)
      }}
    />
  )

  const reprocessStatsButton = permission.update && (
    <ReprocessStats
      key='reprocess'
      id={SCHEDULE_ID}
      onSuccess={triggerRefresh}
    />
  )

  const onPressPrint = async () => {
    try {
      const response = await req(
        `/reports/${SCHEDULE_ID}/registration?format=csv`,
        { parseJSON: false }
      )
      const text = await response.text()
      generateHrefAndDownloadCsv(text, `registration-${new Date().getTime()}`)
    } catch (e) {
      console.error(e)
      alert(
        `There was an error generating the report. ${
          e?.message || ''
        } Please try again.`
      )
    }
  }

  const pages = [
    TOURNAMENT_READY && {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/bracket`,
      text: t('common:playoff', { count: 2 }),
      component: <ScheduleBracket id={SCHEDULE_ID} />,
    },
    {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/schedule`,
      text: `${t('common:schedule')} (${item?.games_count || 0})`,
      component: (
        <GameList
          schedule_id={SCHEDULE_ID}
          slug={`/schedules/${SCHEDULE_ID}/games`}
          refreshKey={refreshKey}
          setVisibleGameIds={(ids) => setVisibleGameIds(ids)}
          areFiltersHidden={areFiltersHidden}
          setFilterForExporting={setFilterForExporting}
          isSoccer={isSoccer}
        />
      ),
      actions: [
        filterToggleButton,
        <BaseActionButton
          key='print'
          disabled={visibleGameIds?.length === 0}
          name='print'
          title='Print Scoresheets'
          onClick={() => {
            if (visibleGameIds?.length > 0) {
              history.push({
                pathname: ROUTES.SCORESHEETS,
                state: { games: visibleGameIds, search: location.search },
              })
            }
          }}
        />,
        permission.update && (
          <FileDownload
            key='download-comp'
            button={(loading, onClick) => {
              return (
                <BaseActionButton
                  loading={loading}
                  onClick={onClick}
                  name='download'
                  title={t('common:exportComp')}
                />
              )
            }}
            url={`/schedules/${SCHEDULE_ID}/export/csv`}
            fileName={item.name_full}
            query={filterForExporting}
          />
        ),
        permission.update && !item?.is_tournament && (
          <ImportCSV
            key='upload-comp'
            button={(label, onClick) => {
              return (
                <BaseActionButton
                  name='upload'
                  title={label}
                  onClick={onClick}
                />
              )
            }}
            entityType={ENTITY_TYPES.schedule}
            id={SCHEDULE_ID}
            supportsXML
            onSuccess={triggerRefresh}
          />
        ),
        permission.update && !item?.is_tournament && (
          <FormSheet
            key='add-game'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={gameForm(
              {
                rootSchedule: item,
                parentId: SCHEDULE_ID,
                onSelectIfNecessary: () =>
                  setIfNecessaryStatus(!ifNecessaryStatus),
                ifNecessaryStatus,
              },
              true
            )}
            label={t('Web:createGame')}
            onSubmit={onAddGame}
            title={t('Web:createANewGame')}
          />
        ),
      ],
    },
    (isCanlan || features?.statistics) && {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/standings`,
      text: t('common:standings'),
      component: (
        <Standings
          scheduleReprocessingListener={scheduleReprocessingListener}
          id={SCHEDULE_ID}
          defaultSort='WPCTD'
          areFiltersHidden={areFiltersHidden}
          setIsFilterToggleDisabled={setIsFilterToggleDisabled}
          refreshKey={refreshKey}
        />
      ),
      actions: [filterToggleButton, reprocessStatsButton],
    },
    (isCanlan || features?.statistics) && {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/statistics`,
      text: t('common:statistics'),
      component: (
        <Statistics
          scheduleReprocessingListener={scheduleReprocessingListener}
          defaultSort='P'
          entityType={ENTITY_TYPES.schedule}
          entityId={SCHEDULE_ID}
          useTeamColumn
          areFiltersHidden={areFiltersHidden}
          setIsFilterToggleDisabled={setIsFilterToggleDisabled}
          refreshKey={refreshKey}
          isSoccer={isSoccer}
        />
      ),
      actions: [filterToggleButton, reprocessStatsButton],
    },
    !isInFrame &&
      (isCanlan || features?.suspensions) && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/suspensions`,
        text: t('common:suspension', { count: 2 }),
        component: (
          <Suspensions
            key={refreshKey}
            exportId={SCHEDULE_ID}
            exportFileName={item?.name_full}
            loading={isSuspensionsLoading}
            suspensions={suspensions}
            refresh={refresh}
            scheduleId={SCHEDULE_ID}
            hasPermission={permission.admin}
            onlyDisplayActive={!permission.admin}
          />
        ),
        actions: [
          permission.admin && (
            <AddSuspensionSheet
              key='add-suspension'
              comp={item}
              onComplete={refresh}
            />
          ),
        ],
      },

    {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/locations`,
      text: t('common:location', { count: 2 }),
      component: (
        <Venues
          entityType={ENTITY_TYPES.schedule}
          id={SCHEDULE_ID}
          slug={`/venues`}
          query={{ schedule_id: SCHEDULE_ID }}
          refreshKey={refreshKey}
          triggerRefresh={triggerRefresh}
          parentPermission={permission}
        />
      ),
      actions: [
        permission.update && (
          <FormSheet
            key='add-venue'
            Button={({ onClick }) => <NewButton onClick={onClick} />}
            form={venueForm({}, SCHEDULE_ID, ENTITY_TYPES.schedule)}
            label={t('Web:createLoc')}
            onSubmit={onAddVenue}
            title={t('Web:createANewLocation')}
          />
          // <NewButton onClick={() => locationModalRef.current.openModal()} />
        ),
      ],
    },
    (isCanlan || features?.announcements) && {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/feed`,
      text: t('common:feed'),
      component: (
        <Home
          isEntityFeed
          entity={item}
          entityType={ENTITY_TYPES.schedule}
          entityId={SCHEDULE_ID}
          refreshKey={refreshKey}
          hideSidebar={!permission.update}
        />
      ),
      actions: [
        permission.update && (
          <Mobile key='add-post'>
            <FormSheet
              label={t('Web:createANewPost')}
              title={t('Web:createANewPost')}
              form={feedItemForm({}, false)}
              onSubmit={async ({ imageData, ...form }) => {
                const body = JSON.stringify(form)
                const response = await req(
                  `/schedules/${SCHEDULE_ID}/social-feed`,
                  {
                    method: 'POST',
                    body,
                  }
                )
                if (imageData) {
                  await req(`/social-feed/${response.data.id}/image`, {
                    method: 'POST',
                    body: imageData,
                  })
                }
                triggerRefresh()
              }}
              Button={({ onClick }) => <NewButton onClick={onClick} />}
            />
          </Mobile>
        ),
      ],
    },
    {
      to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/reports`,
      text: 'Reports',
      component: (
        <NewReports
          entity={item}
          entityType={ENTITY_TYPES.schedule}
          entityId={SCHEDULE_ID}
        />
      ),
      actions: [],
    },
    !isInFrame &&
      permission.update && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/setup`,
        text: t('common:setup'),
        privatePage: true,
        component: (
          <ScheduleTree
            isSoccer={isSoccer}
            hockeySportId={hockeySportId}
            soccerSportId={soccerSportId}
            id={SCHEDULE_ID}
            orgId={item.organization.id}
            schedule={item}
            showSalesforceSettings={syncSalesforce}
          />
        ),
        actions: [
          permission.update && (
            <CloneSchedule
              key='clone'
              schedule={item}
              scheduleId={SCHEDULE_ID}
            />
          ),
          // item.is_tournament && !item.brackets_created && permission.update && (
          //   <CreateBrackets
          //     key='create-brackets'
          //     onCreate={async () => {
          //       await req(`/schedules/${SCHEDULE_ID}/tournaments/create`)
          //       await readItem(ENTITY_TYPES.schedule, SCHEDULE_ID)
          //       setTimeout(() => {
          //         history.push(
          //           `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/gameslots`
          //         )
          //       }, 2000)
          //     }}
          //   />
          // ),
        ],
      },
    !isInFrame &&
      TOURNAMENT_READY &&
      permission.update && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/gameslots`,
        text: t('common:gameSlot', { count: 2 }),
        privatePage: true,
        component: (
          <GameSlots
            id={SCHEDULE_ID}
            canSyncGameSlots={canSyncGameSlots}
            bracketsCreated={item?.brackets_created}
            readItem={readItem}
            setSelectedGameSlotDivision={setSelectedGameSlotDivision}
            setSelectedGameSlotDivisionName={setSelectedGameSlotDivisionName}
            setGamesCreated={setGamesCreated}
            setHasTournamentStarted={setHasTournamentStarted}
            setOnFinishUploadGameslots={setOnFinishUploadGameslots}
          />
        ),
        actions: [
          permission.update && selectedGameSlotDivision && (
            <FileDownload
              key='download-comp'
              button={(loading, onClick) => {
                return (
                  <BaseActionButton
                    disabled={!gamesCreated || hasTournamentStarted}
                    loading={loading}
                    onClick={onClick}
                    name='download'
                    title={t('common:exportComp')}
                  />
                )
              }}
              url={`/schedules/${selectedGameSlotDivision}/gameslots/export/csv`}
              fileName={`${selectedGameSlotDivisionName}`}
              query={filterForExporting}
            />
          ),
          permission.update && selectedGameSlotDivision && (
            <ImportCSV
              key='upload-game-slots'
              button={(label, onClick) => {
                return (
                  <BaseActionButton
                    disabled={!gamesCreated || hasTournamentStarted}
                    name='upload'
                    title={label}
                    onClick={onClick}
                  />
                )
              }}
              entityType={ENTITY_TYPES.gameSlots}
              id={selectedGameSlotDivision}
              supportsXML
              onSuccess={() => {
                triggerRefresh()
                onFinishUploadGameslots()
              }}
              // customOnSuccessMessage={'Refreshing page in 2 seconds...'}
            />
          ),
        ],
      },
    !isInFrame &&
      permission.admin &&
      ENABLE_REGISTRATION &&
      features?.registration &&
      item?.organization.account_subscription_type_id >= 2 && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/registration`,
        text: 'Registration',
        privatePage: true,
        component: (
          <Registration
            key='registration'
            scheduleId={SCHEDULE_ID}
            refreshKey={refreshKey}
          />
        ),
        actions: [
          <BaseActionButton
            key='print'
            name='print'
            title='Download CSV'
            onClick={async () => {
              await onPressPrint()
            }}
          />,
          permission.admin && (
            <EditRegistration
              key='edit-registration'
              scheduleId={SCHEDULE_ID}
            />
          ),
        ],
      },
    !isInFrame &&
      permission.update && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/officials`,
        text: t('common:official', { count: 2 }),
        privatePage: true,
        component: (
          <OfficialList
            key='officials'
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            slug={`/schedules/${SCHEDULE_ID}/officials`}
            refreshKey={refreshKey}
            isSoccer={isSoccer}
          />
        ),
        actions: (isCanlan || features?.rolesPermissions) && [
          <AddOfficial
            key='add-official'
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            permission={permission}
            onComplete={triggerRefresh}
            isSoccer={isSoccer}
          />,
        ],
      },

    !isInFrame &&
      permission.admin && {
        to: `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/users`,
        text: t('common:user', { count: 2 }),
        privatePage: true,
        component: (
          <UserList
            key='users'
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            slug={`/schedules/${SCHEDULE_ID}/permissions`}
            refreshKey={refreshKey}
          />
        ),
        actions: (isCanlan || features?.rolesPermissions) && [
          <AddUser
            key='add-user'
            entityType={ENTITY_TYPES.schedule}
            entityId={SCHEDULE_ID}
            permission={permission}
            onComplete={triggerRefresh}
          />,
        ],
      },
  ]

  let filteredPages = pages

  if (frameConfig?.pages) {
    filteredPages = pages.filter((page) => {
      return frameConfig.pages.includes(page?.text)
    })
  }

  return (
    <>
      <Page
        isTeamOrSchedule
        isSchedule
        error={error}
        loading={loading}
        baseCrumb={
          error
            ? ROUTES.SCHEDULES.ROOT
            : `${ROUTES.ORG_ROOT}/${item?.organization?.id}/schedules`
        }
        baseRoute={BASE_ROUTE}
        baseTitle={item?.organization?.name_full}
        breadcrumbs={getBreadcrumbs(ROUTES.SCHEDULE_ROOT, item, hierarchy)}
        baseActions={baseActions}
        pages={filteredPages}
        isPrivate={!IS_PUBLIC}
        title={item?.name_full || ''}
        iconName={item.is_tournament ? 'trophy' : 'calendar-alt'}
        imageId={getImageThumbnailId(item)}
        titleActions={titleActions}
        preserveQueryFor={[
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/bracket`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/setup`,
          `${ROUTES.SCHEDULE_ROOT}/${SCHEDULE_ID}/gameslots`,
        ]}
      />
      {/* <NewModal ref={locationModalRef} shouldCloseOnOverlayClick={false}>
        <div
          css={css`
            width: 40vw;
            background: no-repeat fixed linear-gradient(#282e38, #181a1d);
            max-height: 80vh;
            border-radius: 8px;
            padding: 20px;
            display: flex;
            flex-direction: column;
            overflow-y: auto;
            transition: all 0.1s ease-in-out;
          `}
        >
          <div
            css={css`
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding-bottom: 16px;
              border-bottom: 1px solid ${colors.SOFT_STEEL};
              margin-bottom: 16px;
            `}
          >
            <h1
              css={css`
                font-size: 36px;
                font-family: ${isCanlan
                  ? 'Neue Plak Condensed, Arial, Helvetica, sans-serif;'
                  : 'Rift, Arial, Helvetica, sans-serif'};
                font-weight: 700;
              `}
            >
              {t('Web:createANewLocation')}
            </h1>
            <button
              onClick={() => locationModalRef.current.closeModal()}
              // disabled={isLoadingVenue}
            >
              <Icon name='times' color={colors.ATTENDANCE_GRAY} fontSize={20} />
            </button>
          </div>
          <div
            css={css`
              flex: 1;
              flex-grow: 1;
              display: flex;
              transition: all 0.1s ease-in-out;
            `}
          >
            {isLoadingVenue ? (
              <div
                css={css`
                  display: flex;
                  justify-content: center;
                  align-items: center;
                  flex: 1;
                `}
              >
                <LoadingSpinner />
              </div>
            ) : (
              <NewLocationAddressSelector
                onCancel={() => locationModalRef.current.closeModal()}
                onSubmit={onAddVenue}
              />
            )}
          </div>
        </div>
      </NewModal> */}
    </>
  )
}

const mapStateToProps = (state) => {
  return {
    hockeySportId: getHockeySport(state)?.id,
    soccerSportId: getSoccerSport(state)?.id,
    isInFrame: state.auth.inFrame,
  }
}

const mapDispatchToProps = (dispatch) => {
  return {
    createVenue: bindActionToPromise(
      dispatch,
      scheduleActions.venues.create.request
    ),
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(Schedule)
